/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { withSentryReactRouterV6Routing, withProfiler } from '@sentry/react';
import { isArray } from 'lodash';
import React from 'react';
import { BrowserRouter, Routes as ReactRoutes, Route } from 'react-router';
import type { AuthenticationConfig } from '@opengeoweb/authentication';
import {
  AuthenticationProvider,
  getAuthConfig,
  Login,
  Logout,
  Code,
} from '@opengeoweb/authentication';
import {
  getLanguageFromLocalStorage,
  RouterWrapperConnect,
} from '@opengeoweb/core';
import type { ConfigType } from '@opengeoweb/shared';
import { AlertBanner } from '@opengeoweb/shared';
import { ThemeWrapper } from '@opengeoweb/theme';
import { workspaceRoutes } from '@opengeoweb/workspace';
import './app.css';
import { useGeowebTranslation } from '../i18n';
import { AppWrapper } from './components/Providers';
import NotFound from './pages/404';
import ErrorPage from './pages/Error';
import Home from './pages/Home';
import { RouteChangeListener } from './pages/RouteChangeListener';
import Workspace from './pages/Workspace';
import { store } from './store';
import type { ValidationError } from './utils/loadConfig';
import {
  isValidConfig,
  isValidConfigWithAuthentication,
  sortErrors,
} from './utils/loadConfig';

interface RoutesProps {
  config?: ConfigType;
}
// ---> Sentry related code
const SentryMonitorReactRoutes = withSentryReactRouterV6Routing(ReactRoutes);
// Sentry related code <--

const Routes: React.FC<RoutesProps> = ({ config }: RoutesProps) => (
  <BrowserRouter>
    <RouterWrapperConnect>
      <>
        <RouteChangeListener
          initialWorkspaceId={config?.GW_INITIAL_WORKSPACE_PRESET}
        />
        <SentryMonitorReactRoutes>
          <Route path={workspaceRoutes.root}>
            <Route element={<Home config={config} />} index />

            <Route
              element={<Workspace config={config} />}
              path={workspaceRoutes.workspace}
            />

            <Route
              element={<Workspace config={config} />}
              path={workspaceRoutes.workspaceDetail}
            />

            <Route element={<Login />} path="/login" />
            <Route element={<Logout />} path="/logout" />
            <Route element={<Code />} path="/code" />
            <Route element={<ErrorPage />} path="/error" />
            <Route element={<NotFound />} path="/*" />
          </Route>
        </SentryMonitorReactRoutes>
      </>
    </RouterWrapperConnect>
  </BrowserRouter>
);

export interface AppProps {
  configObject?: ConfigType;
}

const useHandleInitialLanguage = ({
  configLanguage,
}: {
  configLanguage: string | undefined;
}): void => {
  const { i18n } = useGeowebTranslation();
  const isLanguageIntialised = React.useRef(false);

  if (!isLanguageIntialised.current) {
    const currentLanguage = i18n.language;
    const localStorageLanguage = getLanguageFromLocalStorage();

    // give priority to load language over local storage than config
    const newLanguage = localStorageLanguage ?? configLanguage;
    if (newLanguage && newLanguage !== currentLanguage) {
      i18n.changeLanguage(newLanguage).catch(() => {
        console.warn('Could not change language to:', newLanguage);
      });
    }
    isLanguageIntialised.current = true;
  }
};

const App: React.FC<AppProps> = ({ configObject }: AppProps) => {
  const { t } = useGeowebTranslation();
  useHandleInitialLanguage({ configLanguage: configObject?.GW_LANGUAGE });

  // if config is null, it has not yet loaded.
  if (!configObject) {
    return null;
  }

  // validates config.json. If there are any empty or non existing keys, it will show an error
  const validateRequiredFields = isValidConfig(t, configObject);
  if (validateRequiredFields !== true && isArray(validateRequiredFields)) {
    const errors = sortErrors(validateRequiredFields);

    return (
      <ThemeWrapper>
        <AlertBanner
          info={
            <>
              {Object.keys(errors).map((key) => (
                <p key={key}>
                  <strong>{key}: </strong>
                  {errors[key as keyof typeof errors]!.map(
                    (error: ValidationError) => error.key,
                  ).join(', ')}
                </p>
              ))}
            </>
          }
          severity="error"
          title={t('geoweb-alert-banner-config-not-valid')}
        />
      </ThemeWrapper>
    );
  }

  // validates config.json for valid auth keys. If it's not valid, it will show the default application without requiring authentication
  if (isValidConfigWithAuthentication(configObject) !== true) {
    return (
      <AppWrapper
        defaultThemeName={configObject?.GW_DEFAULT_THEME}
        shouldEnablePluginDebug={configObject?.GW_PLUGIN_DEBUG ?? false}
        store={store}
      >
        <Routes config={configObject} />
      </AppWrapper>
    );
  }
  // renders the validated homepage
  return (
    <AuthenticationProvider
      configURLS={getAuthConfig(configObject as AuthenticationConfig)}
    >
      <AppWrapper
        defaultThemeName={configObject?.GW_DEFAULT_THEME}
        shouldEnablePluginDebug={configObject?.GW_PLUGIN_DEBUG ?? false}
        store={store}
      >
        <Routes config={configObject} />
      </AppWrapper>
    </AuthenticationProvider>
  );
};

export default withProfiler(App);
